import React, { useCallback } from 'react';
import { message, Modal, Input } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import type { ConversationsProps } from '@/types/antdx';
import XAiConversations from '@/components/XAiConversations';
import { useChatState } from '../context/ChatStateContext';
import { useChatActions } from '../context/ChatActionContext';
import { useSession } from '../context/SessionContext';
import { useChatSenderState } from '../context/ChatSenderStateContext';

const { confirm } = Modal;

/**
 * XAdkProvider.Sidebar - 会话列表侧边栏组件
 *
 * 从 Context 获取会话数据并提供会话管理界面
 * 基于 XAiConversations 组件实现(带展开/收起功能)
 *
 * @example
 * ```tsx
 * <XAdkProvider url="..." token="...">
 *   <XAdkProvider.Sidebar isNarrow={false} />
 * </XAdkProvider>
 * ```
 */
export interface XAdkSidebarProps {
  /** 额外的样式 */
  style?: React.CSSProperties;
  /** 额外的类名 */
  className?: string;
  /** 是否为窄屏模式 */
  isNarrow?: boolean;
}

const Sidebar: React.FC<XAdkSidebarProps> = ({
  isNarrow = false,
  style,
  className,
}) => {
  const { currentSessionId } = useChatState();
  const { clearChat } = useChatActions();
  const { sessionList, deleteSession, updateSession, setCurrentSession } = useSession();
  const { setSenderValue } = useChatSenderState();

  // 切换会话
  const handleActiveChange = useCallback((key: string) => {
    setCurrentSession(key);
    setSenderValue('');
  }, [setCurrentSession, setSenderValue]);

  // 新建对话
  const handleNewChat = useCallback(() => {
    clearChat();
    setSenderValue('');
  }, [clearChat, setSenderValue]);

  // 默认会话菜单配置
  const defaultMenuConfig: ConversationsProps['menu'] = useCallback((conversation: any) => ({
    items: [
      {
        label: '重命名',
        key: 'edit',
      },
      {
        label: '删除',
        key: 'delete',
        danger: true,
      },
    ],
    onClick: (menuInfo: any) => {
      menuInfo.domEvent.stopPropagation();
      const { key } = menuInfo;
      switch (key) {
        case 'edit': {
          let inputValue = (conversation.label as string) || '';
          confirm({
            title: '编辑对话名称',
            icon: null,
            content: (
              <Input
                defaultValue={inputValue}
                autoFocus
                style={{ width: '100%' }}
                onChange={(e) => {
                  inputValue = e.target.value;
                }}
              />
            ),
            okText: '确认',
            cancelText: '取消',
            okButtonProps: {
              style: {
                background: '#000',
                borderColor: '#000',
                color: '#fff',
              },
            },
            onOk: () => {
              if (!inputValue.trim()) {
                message.error('对话名称不能为空');
                return Promise.reject();
              }
              updateSession(conversation.key, inputValue);
            },
            onCancel: () => {
              // 静默处理
            },
          });
          break;
        }
        case 'delete':
          confirm({
            title: '确认删除?',
            icon: <ExclamationCircleFilled />,
            content: '该对话内容将被删除无法恢复。',
            okText: '确认删除',
            okType: 'danger',
            cancelText: '取消',
            okButtonProps: {
              style: {
                background: '#ff4d4f',
                borderColor: '#ff4d4f',
                color: '#fff',
              },
            },
            onOk() {
              deleteSession(conversation.key);
            },
            onCancel() {
              //
            },
          });
          break;
      }
    },
  }), [deleteSession, updateSession]);

  return (
    <XAiConversations
      isNarrow={isNarrow}
      sessionList={sessionList}
      activeKey={currentSessionId}
      onActiveChange={handleActiveChange}
      onNewChat={handleNewChat}
      menu={defaultMenuConfig}
      style={style}
    />
  );
};

Sidebar.displayName = 'XAdkProvider.Sidebar';

export default Sidebar;
